/**
 * Right-side drawer with a notification's full detail: header, circuit-breaker
 * callout, delivery timeline, per-channel results, fallback info, retries.
 * Ported from the previous console's LogDetailDrawer (incl. quota-exceeded head).
 */
import React from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import Icon from './Icon';
import { StatusBadge, SourceBadge, ChannelBadge, Copyable } from './badges';
import { Btn } from './ui';
import { useTheme } from '../../context/themeContext';
import { hms, durBetween } from '../../utils/formatters';
import type { NotificationLogUI } from '../../schema/notification';

interface Props {
  notification: NotificationLogUI;
  onClose: () => void;
  onRetry: (n: NotificationLogUI) => void;
}

const CH_STATUS_COLOR: Record<string, string> = {
  delivered: '#2E7D32', failed: '#C0392B', partial: '#B87D0A',
  pending: '#9EA6B8', validation_failed: '#B87D0A', skipped: '#4A8A8A',
};

const SectionTitle: React.FC<{ icon: any; children: React.ReactNode }> = ({ icon, children }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={s.sectionTitle}>
      <Icon name={icon} size={12} color={colors.ink3} />
      <Text style={[s.sectionTitleText, { color: colors.ink3 }]}>{children}</Text>
      <View style={[s.rule, { backgroundColor: colors.line }]} />
    </View>
  );
};

export default function LogDetailDrawer({ notification: n, onClose, onRetry }: Props) {
  const { theme: { colors, font } } = useTheme();
  const t = n.timeline;
  const isFailed = n.status === 'failed' || n.status === 'validation_failed' || n.status === 'quota_exceeded';
  const canRetry = n.status !== 'delivered';
  const cb = n.circuitBreakerInfo;

  const steps = [
    { key: 'queued', date: t.queued, label: 'Queued', done: !!t.queued, delta: null as string | null },
    { key: 'processing', date: t.processing, label: 'Processing', done: !!t.processing, delta: durBetween(t.queued, t.processing) },
    { key: 'terminal', date: t.terminal, label: isFailed ? 'Failed' : n.status === 'partial' ? 'Partial' : 'Delivered', done: !!t.terminal, delta: durBetween(t.processing, t.terminal) },
  ];

  return (
    <View style={s.overlayWrap}>
      <Pressable style={s.scrim} onPress={onClose} />
      <View style={[s.drawer, { backgroundColor: colors.card, borderLeftColor: colors.line }]}>
        {/* Head */}
        <View style={[s.head, { borderBottomColor: colors.line }]}>
          <View style={{ flex: 1 }}>
            <Text style={[s.eyebrow, { color: isFailed ? colors.danger : colors.ink3 }]}>Log Detail</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginVertical: 6 }}>
              <StatusBadge status={n.status} />
              <Copyable value={n.jobId} />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <SourceBadge source={n.source} />
              <Text style={{ color: colors.ink3, fontSize: 12, fontFamily: font.mono }}>{n.referenceId}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12 }}>· template</Text>
              <Text style={{ color: colors.ink, fontSize: 12, fontFamily: font.mono }}>{n.template}</Text>
            </View>
          </View>
          <Pressable onPress={onClose} hitSlop={8}><Icon name="x" size={18} color={colors.ink3} /></Pressable>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, gap: 22 }}>
          {n.circuitBreakerTripped && (
            <View style={[s.callout, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}>
              <Icon name="alert" size={16} color={colors.danger} />
              <View style={{ flex: 1 }}>
                <Text style={{ color: colors.ink, fontWeight: '600', fontSize: 13 }}>Circuit breaker tripped during this delivery.</Text>
                <Text style={{ color: colors.ink2, fontSize: 12, marginTop: 2 }}>
                  Channel <Text style={{ fontFamily: font.mono }}>{cb?.channel || 'unknown'}</Text> · provider <Text style={{ fontFamily: font.mono }}>{cb?.provider || 'unknown'}</Text>
                </Text>
              </View>
            </View>
          )}

          {/* Timeline */}
          <View>
            <SectionTitle icon="activity">Delivery Timeline</SectionTitle>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
              {steps.map((step, i) => (
                <View key={step.key} style={{ flex: 1, alignItems: 'center', gap: 4 }}>
                  <View style={[s.tsDot, {
                    backgroundColor: step.done ? (i === 2 && isFailed ? colors.danger : colors.success) : colors.paper3,
                    borderColor: step.done ? 'transparent' : colors.lineStrong,
                  }]}>
                    {step.done
                      ? <Icon name={i === 2 && isFailed ? 'x' : 'check'} size={13} color="#fff" />
                      : <Text style={{ color: colors.ink4, fontSize: 12 }}>{i + 1}</Text>}
                  </View>
                  <Text style={{ color: colors.ink, fontSize: 12, fontWeight: '600' }}>{step.label}</Text>
                  <Text style={{ color: colors.ink4, fontSize: 11, fontFamily: font.mono }}>{hms(step.date)}</Text>
                  <Text style={{ color: colors.ink4, fontSize: 11 }}>{step.delta || '—'}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Channel results */}
          <View>
            <SectionTitle icon="send">Channel Results</SectionTitle>
            <View style={{ gap: 10, marginTop: 8 }}>
              {n.channels.map((c, i) => (
                <View key={i} style={[s.chResult, { borderColor: colors.line, backgroundColor: colors.paper }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <ChannelBadge channel={c.type} status={c.dotStatus || c.status} size={24} />
                    <Text style={{ color: colors.ink, fontSize: 13, fontWeight: '600', textTransform: 'capitalize', flex: 1 }}>{c.type}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <View style={[s.miniDot, { backgroundColor: CH_STATUS_COLOR[c.status] || colors.ink4 }]} />
                      <Text style={{ color: colors.ink2, fontSize: 12, textTransform: 'capitalize' }}>{c.status.replace('_', ' ')}</Text>
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 24, marginTop: 8 }}>
                    <View><Text style={[s.dt, { color: colors.ink4 }]}>Provider</Text><Text style={{ color: colors.ink2, fontSize: 12 }}>{c.provider || '—'}</Text></View>
                    <View style={{ flex: 1 }}><Text style={[s.dt, { color: colors.ink4 }]}>Message ID</Text>{c.msgId ? <Copyable value={c.msgId} /> : <Text style={{ color: colors.ink4, fontSize: 12 }}>—</Text>}</View>
                  </View>
                  {c.error && (
                    <View style={[s.chError, { backgroundColor: colors.dangerSoft }]}>
                      <Text style={{ color: colors.danger, fontSize: 12 }}><Text style={{ fontWeight: '700' }}>Error: </Text>{c.error}</Text>
                    </View>
                  )}
                </View>
              ))}
            </View>
          </View>

          {/* Fallback */}
          {(n.isFallback || n.shedFallbacks?.length > 0 || n.fallbackSkipReason) && (
            <View>
              <SectionTitle icon="cycle">Fallback Info</SectionTitle>
              <View style={[s.callout, { backgroundColor: '#F5EBFA', borderColor: '#D9BDE9', marginTop: 8 }]}>
                <Icon name="info" size={16} color="#6A1B9A" />
                <View style={{ flex: 1, gap: 4 }}>
                  <Text style={{ color: colors.ink2, fontSize: 12 }}>Is fallback: {n.isFallback ? 'Yes' : 'No'}</Text>
                  {n.shedFallbacks?.length > 0 && <Text style={{ color: colors.ink2, fontSize: 12 }}>Shed channels: {n.shedFallbacks.join(', ')}</Text>}
                  {!!n.fallbackSkipReason && <Text style={{ color: colors.ink2, fontSize: 12 }}>Skip reason: {n.fallbackSkipReason}</Text>}
                </View>
              </View>
            </View>
          )}

          {/* Retries */}
          <View>
            <SectionTitle icon="refresh">Retries</SectionTitle>
            <Text style={{ marginTop: 8, color: colors.ink2, fontSize: 12 }}>
              Attempt{' '}
              <Text style={{ fontFamily: font.mono, fontWeight: '600', color: n.retryCount >= n.retryMax && n.retryMax > 0 ? colors.danger : colors.ink }}>
                {n.retryCount} / {n.retryMax || '—'}
              </Text>
              {n.retryCount >= n.retryMax && n.retryMax > 0 && <Text style={{ color: colors.ink4 }}>  (max attempts reached)</Text>}
            </Text>
          </View>
        </ScrollView>

        <View style={[s.foot, { borderTopColor: colors.line }]}>
          <Btn label="Close" onPress={onClose} />
          <Btn label="Retry Notification" icon="refresh" kind="primary" disabled={!canRetry} onPress={() => onRetry(n)} />
        </View>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  overlayWrap: { ...StyleSheet.absoluteFill, flexDirection: 'row', ...(Platform.OS === 'web' ? { position: 'fixed' as any, zIndex: 200 } : {}) },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.35)' },
  drawer: { width: 480, maxWidth: '92%', borderLeftWidth: 1, height: '100%' },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 20, borderBottomWidth: 1 },
  eyebrow: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6 },
  sectionTitle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  sectionTitleText: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  rule: { flex: 1, height: 1, marginLeft: 4 },
  tsDot: { width: 28, height: 28, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  callout: { flexDirection: 'row', gap: 10, padding: 12, borderRadius: 2, borderWidth: 1 },
  chResult: { padding: 12, borderRadius: 2, borderWidth: 1 },
  miniDot: { width: 8, height: 8, borderRadius: 4 },
  chError: { marginTop: 8, padding: 8, borderRadius: 2 },
  dt: { fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 2 },
  foot: { flexDirection: 'row', justifyContent: 'space-between', padding: 16, borderTopWidth: 1 },
});
